import { useEffect, useState, useMemo } from 'react';
import UniversalRecordTable from '../Tables/universalRecord/UniversalRecordTable';
import { useDocumentSequence } from '../../hooks/document/useDocumentSequence';
import DocumentSequenceRegistrationPopup from './DocumentSequenceRegistrationPopup';
import { useDocumentType } from '../../hooks/useDocumentType';
import { useBranch } from '../../hooks/branches/useBranch';
import { documentSequenceColumns } from '../../features/document/documentSequenceColumns';
import { buildDocumentSequenceFilters } from '../../features/document/documentSequenceFilters';
import { DocumentSequenceSummary } from '../../types/document/DocumentSequenceTypes';
import DocumentSequenceViewPopup from './DocumentSequenceViewPopup';
import { useAllowedOptions } from '../../hooks/settings/useAllowedOptions';
import DocumentSequenceEditPopup from './DocumentSequenceEditPopup';
import { FilterConfig } from '../Tables/advancedRecord/FilterBar';

const DocumentSequenceTableSection: React.FC = () => {
  const {
    list,
    paginator,
    handlerGetFilteredSequences,
    handlerGetById,
    showRegistrationModal,
    openAddSequenceModal,
    closeAddSequenceModal,
    handlerClearFilteredDocumentSequences,
    handlerCreateSequence,
    showViewModal,
    closeViewModal,

    showEditModal,
    closeEditSequenceModal,
    handlerOpenEditModal,
  } = useDocumentSequence();

  const { fetchDocumentTypeDropdown, documentTypeDropdown } = useDocumentType();
  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { getCodes, fetchCodesByModuleAndTarget } = useAllowedOptions();

  const [filters, setFilters] = useState({});
  const [filtersApplied, setFiltersApplied] = useState(false);

  const pageSize = paginator?.size || 10;
  const currentPage = paginator?.number || 0;

  useEffect(() => {
    fetchBranchDropdown();
    fetchDocumentTypeDropdown();
    fetchCodesByModuleAndTarget('AUTHORIZED_SERIES', 'DOCUMENT_TYPE');
  }, []);

  const allowedDocumentCodes = getCodes('AUTHORIZED_SERIES', 'DOCUMENT_TYPE');

  const filteredDocumentTypes = documentTypeDropdown.filter((doc) =>
    allowedDocumentCodes.includes(doc.code)
  );

  const columns = useMemo(() => documentSequenceColumns, []);

  const filterFields: FilterConfig[] = useMemo(
    () => buildDocumentSequenceFilters(branchDropdown, filteredDocumentTypes),
    [branchDropdown, filteredDocumentTypes]
  );

  const getActions = (row: DocumentSequenceSummary) => {
    const actions = [
      {
        key: 'view',
        label: 'Ver',
        onClick: async () => await handlerGetById(row.id),
      },
      {
        key: 'edit',
        label: 'Editar',
        onClick: async () => await handlerOpenEditModal(row.id),
      },
    ];

    return actions;
  };

  const buildPayloadFromFilters = (filters: any) => {
    const payload: any = {};
    if (filters.branchId) payload.branchId = Number(filters.branchId);
    if (filters.documentTypeId)
      payload.documentTypeId = Number(filters.documentTypeId);
    return payload;
  };

  const handleFilterChange = (newFilters: any) => {
    const payload = buildPayloadFromFilters(newFilters);
    setFilters(payload);
    setFiltersApplied(true);
    handlerGetFilteredSequences(payload, 0, pageSize);
  };

  const handlePageChange = (page: number) => {
    handlerGetFilteredSequences(filters, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    handlerGetFilteredSequences(filters, 0, size);
  };

  useEffect(() => {
    if (filtersApplied) {
      handlerGetFilteredSequences(filters, currentPage, pageSize);
    }
  }, [currentPage]);

  return (
    <>
      <UniversalRecordTable
        columns={columns}
        data={list}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filtersConfig={filterFields}
        onFilterChange={handleFilterChange}
        onResetFilters={handlerClearFilteredDocumentSequences}
        getActions={getActions}
        paginator={paginator}
        showPagination={true}
        enableRowClick={true}
        pageSize={pageSize}
        noDataMessage="Utiliza los filtros por sucursal, tipo de documento, serie, estado o fecha de autorización para refinar tu búsqueda."
        topRightAction={{
          label: 'Nuevo Número de Control',
          onClick: openAddSequenceModal,
        }}
      />

      <DocumentSequenceRegistrationPopup
        isOpen={showRegistrationModal}
        onClose={closeAddSequenceModal}
        onSubmit={handlerCreateSequence}
      />

      <DocumentSequenceEditPopup
        isOpen={showEditModal}
        onClose={closeEditSequenceModal}
      />

      <DocumentSequenceViewPopup
        isOpen={showViewModal}
        onClose={closeViewModal}
      />
    </>
  );
};

export default DocumentSequenceTableSection;
